import React from 'react';
import {
  ApartmentOutlined,
  MailOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Col, Modal, Row } from 'antd';
import { Form, useEffectWithPrev } from '@owl-frontend/components';
import { type User, UserRole } from '../../../api/interface';
import { Locale, SessionContext } from '../../../context/AppProvider';
import { FeatureKeys } from '../../../data/session/interface';
import useUsersHook from './useUsersHook';
import styles from './UsersContainer.module.scss';

const REGEX_CLIENT_ID = /^\d+$/;

export interface UserForm {
  first_name: string;
  last_name: string;
  email: string;
  client_user_id: string;
  roles: string[];
}

interface UserCreateFormProps {
  existingEmails: string[];
  users: User[];
  isModalVisible: boolean;
  setIsModalVisible(isModalVisible: boolean): void;
}

const UserCreateForm: Form.MFC<UserCreateFormProps, UserForm> = ({
  existingEmails,
  isModalVisible,
  setIsModalVisible,
  users,
  // RFF
  invalid,
  pristine,
  form,
  handleSubmit,
}) => {
  const { messages } = React.useContext(Locale);
  const { features } = React.useContext(SessionContext);
  const [
    {
      actions: { createUser },
    },
  ] = useUsersHook();

  const onCancel = () => {
    form.restart();
    setIsModalVisible(false);
  };

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus == 'pending' && createUser.status === 'fulfilled') {
        form.restart();
      }
    },
    [createUser.status, form]
  );

  const validateClientUserId = React.useCallback(
    (value: string): string | undefined => {
      if (!value) {
        return;
      }

      if (
        features[FeatureKeys.CustomerTerminology] &&
        REGEX_CLIENT_ID.test(value)
      ) {
        return messages['users.validators.employee_id.numeric_only'];
      }

      return undefined;
    },
    [features, messages]
  );

  return (
    <Modal
      data-testid="user-modal"
      maskClosable={false}
      title="New User"
      open={isModalVisible}
      onCancel={onCancel}
      width={400}
      footer={[
        <Button key="cancel" onClick={onCancel} data-testid="cancel-button">
          {messages['users.create.buttons.cancel']}
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleSubmit}
          data-testid="create-button"
          disabled={invalid || pristine}
        >
          {messages['users.create.buttons.add']}
        </Button>,
      ]}
    >
      <form onSubmit={handleSubmit}>
        <Row gutter={12} className={styles.row}>
          <Col span={12}>
            <Form.InputManaged
              name="first_name"
              label={messages['users.create.modal.first_name.label']}
              data-testid="first-name"
              prefix={<UserOutlined className={styles.inputIcon} />}
              placeholder={
                messages['users.create.modal.first_name.placeholder']
              }
            />
          </Col>
          <Col span={12}>
            <Form.InputManaged
              name="last_name"
              label={messages['users.create.modal.last_name.label']}
              data-testid="last-name"
              prefix={<UserOutlined className={styles.inputIcon} />}
              placeholder={messages['users.create.modal.last_name.placeholder']}
            />
          </Col>
        </Row>
        <Form.InputManaged
          name="email"
          label={messages['users.create.modal.email.label']}
          placeholder={messages['users.create.modal.email.placeholder']}
          data-testid="user-email"
          prefix={<MailOutlined className={styles.inputIcon} />}
          validate={Form.Validators.combineValidators(
            [
              Form.Validators.email({
                errorMessage: messages['users.validators.email.invalid'],
              }),
            ].concat(
              Form.Validators.notIn({
                values: existingEmails,
                errorMessage: messages['users.validators.email.unique'],
              })
            )
          )}
        />

        <Form.InputManaged
          name="client_user_id"
          label={messages['users.create.modal.employee_id.label']}
          data-testid="user-id"
          prefix={<ApartmentOutlined className={styles.inputIcon} />}
          placeholder={messages['users.create.modal.employee_id.placeholder']}
          validate={Form.Validators.combineValidators([
            validateClientUserId,
            Form.Validators.notIn({
              values: users.map((u) => u.client_user_id),
              errorMessage: messages['users.validators.employee_id.unique'],
            }),
          ])}
        />

        <Form.SelectAntdManaged
          name="roles"
          label={messages['users.create.modal.role.label']}
          placeholder={messages['users.create.modal.role.placeholder']}
          data-testid="user-roles"
          options={[
            {
              label: messages['users.create.modal.role.options.assignee'],
              value: UserRole.Reviewer,
            },
            {
              label: messages['users.create.modal.role.options.manager'],
              value: UserRole.Manager,
            },
          ]}
        />
      </form>
    </Modal>
  );
};

const UserCreateFormManaged = Form.manage(UserCreateForm);

interface NewUserModalProps {
  isModalVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  createUser: (data: UserForm) => void;
  existingEmails: string[];
  users: User[];
}

const NewUserModal: React.FC<NewUserModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  createUser,
  existingEmails,
  users = [],
}) => {
  return (
    <UserCreateFormManaged
      onSubmit={createUser}
      existingEmails={existingEmails}
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      users={users}
    />
  );
};

export default NewUserModal;
